import {useTranslation} from "react-i18next";
import {Button} from "@mantine/core";
import {DIBDocumentGeneratorReset} from "@dibkunnskap/dokumentgenerator";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {useReportAccess} from "./AccessContext";
import {ResourceType} from "./types";
import {DocumentGeneratorBody} from "./docgen/DocumentGeneratorBody";
import {GenerateButtons} from "./generate/GenerateButtons";
import {RelatedContentLEGACY} from "./legacy/RelatedContentLEGACY";
import {SupplierInfoLEGACY} from "./legacy/SupplierInfoLEGACY";
import {renderNode} from "./legacy/renderNodeLegacy";
import {useContentTitle} from "./useContentTitle";
import classes from "./DocumentGeneratorResource.module.css";

export const DocumentGeneratorResource = ({
    resourceId,
}: {
    resourceId: string;
    // isPreview is accepted from the route switch, but getDocgenDocument has no
    // preview-specific request shape today — wire it in if /preview/:topicId
    // for a docgen topic ever needs a different payload.
    isPreview: boolean;
}) => {
    const {t} = useTranslation();

    // RTK Query dedupes against the identical call inside DocumentGeneratorBody
    // (both pass collectionId undefined) — one network request, one cache entry.
    const {data: docData, isFetching} =
        dibAppApi.endpoints.getDocgenDocument.useQuery(
            {resourceId, collectionId: undefined},
            {refetchOnMountOrArgChange: false} // standalone: don't force refetch
        );

    const topicData = docData?.topicData;

    useContentTitle(topicData?.name);

    useReportAccess(resourceId, topicData?.access);

    const topOfForm = (
        <DIBDocumentGeneratorReset
            text={t("Action.ResetFields", {defaultValue: "Reset fields"})}
            onRender={({onClick, buttonText}) => (
                <Button
                    variant="subtle"
                    color="dark"
                    size="compact-sm"
                    onClick={onClick}
                >
                    {buttonText}
                </Button>
            )}
        />
    );

    // Render the `xdesc;` (desccontainer) node itself, matching DibApp2020's
    // bk-viewer, so the `.desccontainer` border-bottom applies. Skip entries whose
    // container has no children to avoid an empty bordered box.
    const topOfPreview = topicData ? (
        <>
            <h1>{topicData.name}</h1>
            {topicData.descroot?.flatMap((entry) => {
                const el = topicData.elements[entry.id];
                if (!el?.children?.length) return [];
                return [renderNode(topicData, entry.id, {}, entry.id)];
            })}
        </>
    ) : null;

    const bottomOfForm = topicData ? (
        <GenerateButtons
            isStandalone
            isFetching={isFetching}
            topicMeta={{
                topicId: topicData.topicId,
                resourceId: topicData.id,
                resourceTypeId: ResourceType.documentGenerator,
                name: topicData.name,
            }}
            documents={[
                {
                    id: topicData.id,
                    name: topicData.name,
                    selected: true,
                    resourceTypeId: ResourceType.documentGenerator,
                },
            ]}
        />
    ) : null;

    return (
        <div className={classes.page}>
            <main className={classes.main}>
                <DocumentGeneratorBody
                    documentId={resourceId}
                    isDraft={false}
                    topOfForm={topOfForm}
                    topOfPreview={topOfPreview}
                    bottomOfForm={bottomOfForm}
                />
            </main>
            <aside className={classes.aside}>
                {topicData && (
                    <>
                        <SupplierInfoLEGACY document={topicData} />
                        <RelatedContentLEGACY document={topicData} />
                    </>
                )}
            </aside>
        </div>
    );
};
